import { useAuth } from "@/hooks/auth";
import { useNavigate } from "@tanstack/react-router";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";

interface CustomerIdContextType {
  customerId: string | null;
  setCustomerId: (id: string | null, withNavigateMainPage?: boolean) => void;
}

const CustomerIdContext = createContext<CustomerIdContextType | undefined>(
  undefined
);

export const CustomerIdProvider = ({ children }: { children: ReactNode }) => {
  const [customerId, setCustomerId] = useState<string | null>(null);
  const navigate = useNavigate();
  const {
    isLoading,
    isAuthenticated,
    isCustomerUser,
    customerId: customerIdFromAuth,
  } = useAuth();

  const setCustomerIdWithNavigate = useCallback(
    (newValue: string | null, withNavigateMainPage = false) => {
      if (customerId === newValue) return;
      if (withNavigateMainPage) {
        navigate({ to: "/" })
          .catch((err) => {
            console.error("Navigation failed:", err);
          })
          .finally(() => {
            setCustomerId(newValue);
          });
      } else {
        setCustomerId(newValue);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (
      isCustomerUser &&
      !isLoading &&
      isAuthenticated &&
      customerIdFromAuth &&
      customerId !== customerIdFromAuth
    ) {
      setCustomerIdWithNavigate(customerIdFromAuth);
    }
  }, [isCustomerUser, isLoading, customerIdFromAuth, customerId]);

  return (
    <CustomerIdContext.Provider
      value={{ customerId, setCustomerId: setCustomerIdWithNavigate }}
    >
      {children}
    </CustomerIdContext.Provider>
  );
};

export function useCustomerId() {
  const context = useContext(CustomerIdContext);
  if (!context) {
    throw new Error("useCustomerId must be used within a CustomerIdProvider");
  }
  return context;
}
